import React, { useEffect, useMemo, useRef, useState } from "react";
import { animate, stagger } from "animejs";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";
import MapRouteSection from "../components/MapRouteSection";

function BackgroundObject({
  targetX,
  targetScale,
}: {
  targetX: number;
  targetScale: number;
}) {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((_, delta) => {
    if (!meshRef.current) return;
    meshRef.current.rotation.y += delta * 0.18;
    meshRef.current.rotation.x += delta * 0.06;

    const lerpFactor = 1 - Math.pow(0.001, delta);
    meshRef.current.position.x = THREE.MathUtils.lerp(
      meshRef.current.position.x,
      targetX,
      lerpFactor
    );
    const nextScale = THREE.MathUtils.lerp(meshRef.current.scale.x, targetScale, lerpFactor);
    meshRef.current.scale.setScalar(nextScale);
  });

  return (
    <mesh ref={meshRef}>
      <icosahedronGeometry args={[1.6, 1]} />
      <meshBasicMaterial color="#3a4a5e" wireframe transparent opacity={0.55} />
    </mesh>
  );
}

export default function Test3DExperiencePage() {
  const pageRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [totalSections, setTotalSections] = useState(1);

  const stats = useMemo(
    () => [
      {
        value: "+10%",
        label: "Enterprise value",
        note: "delle bionic company supera questa soglia di crescita (BCG, 2024)",
      },
      {
        value: "66%",
        label: "ROI digital",
        note: "delle organizzazioni mature digitalmente riporta ROI ≥10% sui progetti",
      },
      {
        value: "90%",
        label: "KPI con AI",
        note: "delle organizzazioni che usano AI per definire i KPI ne riporta un miglioramento",
      },
      {
        value: "1,5×",
        label: "Probabilità di successo",
        note: "ridefinire ruoli e responsabilità raddoppia quasi le chance di successo (McKinsey)",
      },
    ],
    []
  );

  const principles = useMemo(
    () => [
      {
        title: "Approccio per domini",
        body: "Non basta digitalizzare singoli use-case: la trasformazione funziona quando si interviene su interi domini — un customer journey end-to-end, un processo, un'area funzionale. Frammentare il perimetro produce effetti locali che si dissolvono nel tempo.",
      },
      {
        title: "Metriche nelle tre dimensioni",
        body: "I KPI di una trasformazione si distribuiscono su tre fronti: creazione di valore (impatto operativo e finanziario), salute del team (capacità, staffing, ritmo) e progresso del cambiamento (adozione, capability building).",
      },
      {
        title: "Persone prima della tecnologia",
        body: "Sviluppare competenze e ridefinire i ruoli interni è il fattore più correlato al successo. La tecnologia abilita, ma è la struttura delle responsabilità a determinare se il cambiamento radica o evapora.",
      },
      {
        title: "Commitment della leadership",
        body: "Senza un mandato chiaro e visibile da CEO e top management — con una visione comunicata e ripetuta — la trasformazione si arena alla prima frizione organizzativa. Il digitale deve essere priorità non negoziabile.",
      },
      {
        title: "Struttura agile in rete",
        body: "Le aziende che riescono adottano un'organizzazione a rete, costruita intorno alle fonti di valore, con team agili che attraversano i silos funzionali. La gerarchia tradizionale rallenta la velocità di apprendimento.",
      },
    ],
    []
  );

  const phases = useMemo(
    () => [
      {
        step: "Fase 01",
        title: "Imagine & Frame",
        body: "Discovery del contesto, mappatura degli stakeholder, identificazione del valore atteso e framing di una soluzione coerente con la strategia. Output: ipotesi di lavoro validate, business case iniziale, perimetro chiaro.",
        bullets: [
          "Workshop di discovery con i decisori",
          "Customer & process mapping",
          "Business case e ipotesi di valore",
        ],
      },
      {
        step: "Fase 02",
        title: "Design & Deliver",
        body: "Disegno operativo della soluzione, prototipi rapidi, sprint di delivery con deliverable verificabili. Si lavora a finestre brevi (2–3 settimane) con metriche concordate per ogni iterazione.",
        bullets: [
          "Architettura tecnica e di prodotto",
          "Sprint di sviluppo e validazione",
          "Roll-out controllato per coorti",
        ],
      },
      {
        step: "Fase 03",
        title: "Scale & Sustain",
        body: "Estensione a scala completa, governance del valore creato e capability building interno. La trasformazione si stabilizza quando il cliente è in grado di evolvere la soluzione senza dipendere dal partner esterno.",
        bullets: [
          "Playbook operativo e formazione",
          "Control tower dei KPI",
          "Roadmap di evoluzione 12–24 mesi",
        ],
      },
    ],
    []
  );

  const kpiBuckets = useMemo(
    () => [
      {
        label: "Value creation",
        body: "Le metriche operative che si traducono in beneficio finanziario: tempo di ciclo, conversion rate, costo per transazione, retention. Un progetto digitale ben impostato ne presidia una o due alla volta, con baseline misurata e target dichiarato.",
      },
      {
        label: "Team health",
        body: "Le trasformazioni rallentano quando i team sono sotto-staffati o privi delle capability critiche. Misurare salute del team — copertura skill, dipendenze esterne, ritmo di delivery — anticipa i blocchi prima che diventino strutturali.",
      },
      {
        label: "Change management",
        body: "Il progresso del cambiamento stesso: adozione delle nuove pratiche, livello di capability acquisite internamente, qualità della comunicazione tra business e tech. È la metrica che spiega perché due trasformazioni con stessa tecnologia danno risultati opposti.",
      },
    ],
    []
  );

  const sectionsExpanded = useMemo(
    () => [
      {
        id: "services",
        kicker: "Servizi",
        title: "Servizi end-to-end",
        body: "Strategia, design e ingegneria coordinati in un unico flusso, dalla discovery al rilascio in produzione e oltre.",
        details:
          "Il modello a partner unico riduce i tempi di handoff tra fasi e mantiene una memoria di progetto unificata. Niente brief tradotti tre volte, niente decisioni perse tra documenti. Lavoriamo con team integrati cliente-partner, in cui un product owner del cliente partecipa allo stand-up giornaliero e ha potere decisionale operativo.",
        list: [
          "Discovery e business case",
          "Product strategy e roadmap",
          "Design system e UI",
          "Ingegneria full-stack",
          "Rilascio, monitoraggio, evoluzione",
        ],
      },
      {
        id: "approach",
        kicker: "Approccio",
        title: "Decisioni data-driven",
        body: "Ogni scelta di prodotto è validata su dato — qualitativo o quantitativo — non su opinione del più senior in stanza.",
        details:
          "Validazione continua significa cicli brevi tra ipotesi, esperimento e lettura dei risultati. Strumentiamo l'esperienza utente fin dal primo rilascio, con event tracking definito insieme al business e dashboard condivise. Le ipotesi che non superano la soglia concordata vengono pivotate o spente, senza romanticismi.",
        list: [
          "Tracciamento eventi sin dal MVP",
          "Test A/B e cohort analysis",
          "Interviste utente strutturate",
          "Decision log condiviso",
          "Pivoting senza attriti politici",
        ],
      },
      {
        id: "process",
        kicker: "Processo",
        title: "Metodo strutturato e leggero",
        body: "Sprint brevi, deliverable verificabili, owner chiari per ogni fase. Niente cerimoniali fini a sé stessi.",
        details:
          "L'agile applicato ai progetti enterprise rischia spesso di degenerare in rituali svuotati di contenuto. Manteniamo il minimo indispensabile: planning bisettimanale, demo aperte agli stakeholder, retrospettive quindicinali. Tutto ciò che non porta decisioni o apprendimento viene tagliato.",
        list: [
          "Sprint di 10 giorni lavorativi",
          "Demo pubblica a fine sprint",
          "Retrospective con action item tracciati",
          "Documentazione vivente",
          "Stand-up brevi, focalizzati sui blocchi",
        ],
      },
      {
        id: "results",
        kicker: "Risultati",
        title: "KPI condivisi e misurabili",
        body: "Obiettivi quantificati a inizio progetto e revisione trasparente al termine di ogni iterazione.",
        details:
          "I KPI vengono dichiarati prima del kickoff e diventano la lingua franca tra cliente e team. Un control tower leggero — una dashboard sola, non una suite — rende lo stato visibile a tutti senza riunioni di status. Il successo non è il rilascio: è lo spostamento misurabile della metrica scelta.",
        list: [
          "KPI dichiarati al kickoff",
          "Baseline misurata, non stimata",
          "Dashboard unica e condivisa",
          "Review mensile con stakeholder",
          "Report finale con lessons learned",
        ],
      },
    ],
    []
  );

  const faq = useMemo(
    () => [
      {
        q: "Quanto dura un progetto tipico?",
        a: "Una discovery va da 3 a 6 settimane. Un primo MVP rilasciabile in genere si inquadra in 3–4 mesi. La fase di scale-up dipende dall'ampiezza del dominio coinvolto e dalla maturità organizzativa interna.",
      },
      {
        q: "Lavorate con team interni?",
        a: "Sempre. Il modello blended — sviluppatori e designer del partner accanto a figure interne — è quello che produce capability building reale. Operare in isolamento è veloce ma fragile a medio termine.",
      },
      {
        q: "Come gestite la proprietà intellettuale?",
        a: "Il codice e i deliverable sono del cliente dal primo commit. Manteniamo un nostro layer di librerie e tooling interno, ma il prodotto finale è interamente trasferibile.",
      },
      {
        q: "Cosa succede dopo il rilascio?",
        a: "Offriamo una finestra di hyper-care di 4–8 settimane post go-live, durante la quale stabilizziamo il sistema e formiamo il team interno. Successivamente passiamo a un modello di evoluzione a sprint dedicati o ci ritiriamo se il cliente è autosufficiente.",
      },
      {
        q: "Lavorate in modalità fixed-price o time & material?",
        a: "Fixed-price su perimetri molto ben definiti (tipicamente la discovery e il primo MVP), time & material sulle fasi di evoluzione dove la flessibilità ha più valore della prevedibilità del prezzo.",
      },
      {
        q: "Su quali settori avete più esperienza?",
        a: "Servizi finanziari, retail, energy e industrial. Ogni settore ha vincoli regolatori e ritmi propri: il framework è invariante, i tempi e i pesi delle fasi cambiano.",
      },
    ],
    []
  );

  useEffect(() => {
    const scrollContainer = document.getElementById("app-scroll");
    if (!scrollContainer) return;
    scrollContainer.classList.add("snap-scroll-enabled");
    return () => {
      scrollContainer.classList.remove("snap-scroll-enabled");
    };
  }, []);

  useEffect(() => {
    const scrollContainer = document.getElementById("app-scroll");
    const page = pageRef.current;
    if (!scrollContainer || !page) return;

    const els = Array.from(page.querySelectorAll<HTMLElement>(".test-snap-section"));
    if (els.length === 0) return;
    setTotalSections(els.length);
    els.forEach((el, i) => {
      el.dataset.snapIndex = String(i);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idx = Number((entry.target as HTMLElement).dataset.snapIndex);
          if (!Number.isNaN(idx)) setActiveIndex(idx);
        });
      },
      { root: scrollContainer, rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    els.forEach((el) => observer.observe(el));

    const scrollToSection = (idx: number) => {
      const target = els[Math.max(0, Math.min(els.length - 1, idx))];
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;

      const keyToDelta: Record<string, number> = {
        ArrowDown: 1,
        PageDown: 1,
        " ": e.shiftKey ? -1 : 1,
        ArrowUp: -1,
        PageUp: -1,
        Home: -Infinity,
        End: Infinity,
      };
      const delta = keyToDelta[e.key];
      if (delta === undefined) return;
      e.preventDefault();
      if (delta === Infinity) scrollToSection(els.length - 1);
      else if (delta === -Infinity) scrollToSection(0);
      else {
        const currentIdx = Number(
          els.reduce((closest, el) => {
            const rect = el.getBoundingClientRect();
            const closestRect = closest.getBoundingClientRect();
            return Math.abs(rect.top) < Math.abs(closestRect.top) ? el : closest;
          }, els[0]).dataset.snapIndex ?? 0
        );
        scrollToSection(currentIdx + delta);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      observer.disconnect();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    let teleporting = false;
    let scrollTimer: ReturnType<typeof setTimeout> | null = null;
    let cleanupListeners: (() => void) | null = null;

    const setup = () => {
      const cards = Array.from(el.querySelectorAll<HTMLElement>("[data-carousel-card]"));
      if (cards.length < 3) return null;
      const copyLen = cards.length / 3;
      if (!Number.isInteger(copyLen) || copyLen < 1) return null;

      const w = el.clientWidth;
      if (!w) return null;

      let pct = 0.8;
      if (w >= 1024) pct = 0.42;
      else if (w >= 768) pct = 0.6;
      const cardWidth = Math.floor(w * pct);
      cards.forEach((c) => {
        c.style.width = `${cardWidth}px`;
      });

      const centerScroll = (c: HTMLElement) =>
        c.offsetLeft + c.offsetWidth / 2 - el.clientWidth / 2;

      el.scrollLeft = centerScroll(cards[copyLen]);

      const findClosestIdx = () => {
        const vc = el.scrollLeft + el.clientWidth / 2;
        let closestIdx = 0;
        let minDist = Infinity;
        for (let i = 0; i < cards.length; i++) {
          const cc = cards[i].offsetLeft + cards[i].offsetWidth / 2;
          const dist = Math.abs(cc - vc);
          if (dist < minDist) {
            minDist = dist;
            closestIdx = i;
          }
        }
        return closestIdx;
      };

      const teleportIfBuffer = (instant: boolean) => {
        const closestIdx = findClosestIdx();
        let targetIdx: number | null = null;
        if (closestIdx < copyLen) targetIdx = closestIdx + copyLen;
        else if (closestIdx >= 2 * copyLen) targetIdx = closestIdx - copyLen;
        if (targetIdx === null) return;
        teleporting = true;
        const target = centerScroll(cards[targetIdx]);
        if (instant) {
          el.scrollLeft = target;
        } else {
          el.scrollLeft = target;
        }
        requestAnimationFrame(() => {
          teleporting = false;
        });
      };

      const eagerTeleportThreshold = copyLen / 2;
      const eagerCheck = () => {
        if (teleporting) return;
        const closestIdx = findClosestIdx();
        if (
          closestIdx < eagerTeleportThreshold ||
          closestIdx >= 3 * copyLen - eagerTeleportThreshold
        ) {
          teleportIfBuffer(true);
        }
      };

      const onScroll = () => {
        if (teleporting) return;
        eagerCheck();
        if (scrollTimer) clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => teleportIfBuffer(false), 80);
      };
      el.addEventListener("scroll", onScroll, { passive: true });

      let isDragging = false;
      let dragMoved = false;
      let startX = 0;
      let startScrollLeft = 0;
      let activePointerId: number | null = null;

      const snapToNearest = () => {
        const idx = findClosestIdx();
        el.scrollTo({ left: centerScroll(cards[idx]), behavior: "smooth" });
      };

      const onPointerDown = (e: PointerEvent) => {
        if (e.pointerType !== "mouse") return;
        if (e.button !== 0) return;
        isDragging = true;
        dragMoved = false;
        startX = e.clientX;
        startScrollLeft = el.scrollLeft;
        activePointerId = e.pointerId;
        try {
          el.setPointerCapture(e.pointerId);
        } catch {
          // ignore
        }
      };

      const onPointerMove = (e: PointerEvent) => {
        if (!isDragging || e.pointerId !== activePointerId) return;
        const dx = e.clientX - startX;
        if (!dragMoved && Math.abs(dx) > 4) {
          dragMoved = true;
          el.style.cursor = "grabbing";
        }
        if (dragMoved) {
          e.preventDefault();
          el.scrollLeft = startScrollLeft - dx;
        }
      };

      const endDrag = (e: PointerEvent) => {
        if (!isDragging || e.pointerId !== activePointerId) return;
        const wasMoved = dragMoved;
        isDragging = false;
        dragMoved = false;
        activePointerId = null;
        el.style.cursor = "";
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {
          // ignore
        }
        if (wasMoved) {
          snapToNearest();
        }
      };

      el.addEventListener("pointerdown", onPointerDown);
      el.addEventListener("pointermove", onPointerMove);
      el.addEventListener("pointerup", endDrag);
      el.addEventListener("pointercancel", endDrag);

      const preventDragImage = (e: DragEvent) => e.preventDefault();
      el.addEventListener("dragstart", preventDragImage);

      return () => {
        el.removeEventListener("scroll", onScroll);
        el.removeEventListener("pointerdown", onPointerDown);
        el.removeEventListener("pointermove", onPointerMove);
        el.removeEventListener("pointerup", endDrag);
        el.removeEventListener("pointercancel", endDrag);
        el.removeEventListener("dragstart", preventDragImage);
        if (scrollTimer) {
          clearTimeout(scrollTimer);
          scrollTimer = null;
        }
        el.style.cursor = "";
      };
    };

    const init = () => {
      if (cleanupListeners) cleanupListeners();
      cleanupListeners = setup();
    };

    const t = setTimeout(init, 50);

    const ro = new ResizeObserver(() => init());
    ro.observe(el);

    return () => {
      clearTimeout(t);
      if (cleanupListeners) cleanupListeners();
      ro.disconnect();
    };
  }, []);

  const { targetX, targetScale } = useMemo(() => {
    if (activeIndex <= 0) return { targetX: 0, targetScale: 1 };
    const denom = Math.max(1, totalSections - 2);
    const progress = Math.min(1, (activeIndex - 1) / denom);
    return {
      targetX: -2 + progress * 5,
      targetScale: 1 + progress * 0.7,
    };
  }, [activeIndex, totalSections]);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const page = pageRef.current;
    if (!page || prefersReducedMotion) return;

    const sectionsEls = Array.from(page.querySelectorAll<HTMLElement>(".snap-reveal"));
    if (sectionsEls.length === 0) return;

    sectionsEls.forEach((el) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(36px)";
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const children = (entry.target as HTMLElement).querySelectorAll(".stagger-item");
          animate(entry.target as HTMLElement, {
            opacity: [0, 1],
            translateY: ["36px", "0px"],
            duration: 760,
            ease: "outExpo",
          });
          if (children.length > 0) {
            animate(children, {
              opacity: [0, 1],
              translateY: ["18px", "0px"],
              delay: stagger(70),
              duration: 680,
              ease: "outQuad",
            });
          }
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.18 }
    );
    sectionsEls.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={pageRef}
      className="relative mx-auto flex w-full max-w-6xl flex-col px-4 pt-20 md:px-8"
    >
      <div className="corporate-bg pointer-events-none fixed inset-0 -z-20" aria-hidden />
      <div className="pointer-events-none fixed inset-0 -z-10" aria-hidden>
        <Canvas
          camera={{ position: [0, 0, 5], fov: 42 }}
          gl={{ antialias: true, alpha: true }}
          dpr={[1, 2]}
        >
          <ambientLight intensity={0.8} />
          <BackgroundObject targetX={targetX} targetScale={targetScale} />
        </Canvas>
      </div>

      {/* HERO */}
      <section className="test-snap-section flex min-h-[100dvh] flex-col justify-center gap-8 py-20">
        <header className="snap-reveal space-y-6">
          <p className="stagger-item text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-400">
            Corporate · Business · Test page
          </p>
          <h1 className="stagger-item text-balance text-4xl font-extrabold leading-[1.05] text-white md:text-6xl lg:text-7xl">
            Soluzioni digitali, costruite con metodo.
          </h1>
          <p className="stagger-item max-w-3xl text-base leading-relaxed text-zinc-400 md:text-lg">
            Strategia, design e ingegneria allineati intorno a obiettivi misurabili. Una pagina di esempio che riprende
            principi e framework di consulenza pubblicati da McKinsey, Deloitte e Boston Consulting Group, riadattati
            in un linguaggio operativo.
          </p>
          <div className="stagger-item flex flex-wrap gap-3 pt-2">
            <a
              className="rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#0a0a0c] transition hover:bg-zinc-200"
              href="#metodo"
            >
              Scopri il metodo
            </a>
            <a
              className="rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-zinc-200 transition hover:border-white/30 hover:text-white"
              href="/contact"
            >
              Parliamone
            </a>
          </div>
        </header>
      </section>

      {/* STATS BAR */}
      <section className="test-snap-section snap-reveal flex min-h-[100dvh] flex-col justify-center gap-10 border-t border-white/[0.08] py-16">
        <header className="stagger-item max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Numeri di settore</p>
          <h2 className="mt-3 text-3xl font-bold text-white md:text-4xl">
            Quattro indicatori per inquadrare la conversazione
          </h2>
          <p className="mt-4 text-zinc-400 md:text-lg">
            Sintesi di metriche di benchmark estratte da rapporti pubblici di McKinsey e BCG sulla maturità digitale
            delle organizzazioni.
          </p>
        </header>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="stagger-item space-y-2">
              <p className="text-4xl font-extrabold tracking-tight text-white md:text-5xl">{s.value}</p>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-zinc-300">{s.label}</p>
              <p className="text-sm leading-relaxed text-zinc-500">{s.note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MAP ROUTE */}
      <MapRouteSection />

      {/* MANIFESTO 2-COL */}
      <section
        id="metodo"
        className="test-snap-section snap-reveal flex min-h-[100dvh] flex-col justify-center gap-10 border-t border-white/[0.08] py-20 md:grid md:grid-cols-12 md:items-center"
      >
        <div className="stagger-item md:col-span-5">
          <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Manifesto</p>
          <h2 className="mt-3 text-3xl font-bold leading-tight text-white md:text-4xl">
            Il digitale non è un progetto. È una capability che si costruisce.
          </h2>
        </div>
        <div className="stagger-item space-y-4 text-zinc-400 md:col-span-7 md:text-lg">
          <p>
            La maggior parte delle iniziative di trasformazione fallisce per ragioni che non sono tecniche. Mancanza
            di mandato esecutivo, perimetri scelti per opportunità politica anziché per valore, KPI scollegati dalla
            strategia e team-of-one bloccati dai silos.
          </p>
          <p>
            La nostra pratica nasce da questa lettura. Lavoriamo con un perimetro di lavoro stretto e un commitment
            largo: poco scope, molta convinzione. La tecnologia entra dopo che il problema è stato compreso, non
            prima.
          </p>
          <p>
            Le pagine successive sintetizzano in modo operativo principi che la letteratura di management consolidata
            ripete da quasi un decennio. Non c'è nulla di esoterico — solo poche cose, fatte bene.
          </p>
        </div>
      </section>

      {/* 5 LEVE GRID */}
      <section className="test-snap-section snap-reveal flex min-h-[100dvh] flex-col justify-center border-t border-white/[0.08] py-20">
        <header className="stagger-item mb-12 max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Cinque leve</p>
          <h2 className="mt-3 text-3xl font-bold text-white md:text-4xl">
            Cosa distingue una trasformazione che riesce
          </h2>
          <p className="mt-4 text-zinc-400 md:text-lg">
            Sintesi adattata dai principi pubblicati da McKinsey & Company sulla riuscita delle trasformazioni
            digitali e organizzative.
          </p>
        </header>
        <div
          ref={carouselRef}
          className="carousel-track relative cursor-grab select-none snap-x snap-mandatory overflow-x-auto"
          style={{
            maskImage:
              "linear-gradient(to right, transparent 0%, black 18%, black 82%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, black 18%, black 82%, transparent 100%)",
          }}
        >
          <div className="flex gap-6 py-2">
            {[...principles, ...principles, ...principles].map((p, i) => {
              const originalIndex = i % principles.length;
              return (
                <article
                  key={`${p.title}-${i}`}
                  data-carousel-card
                  className="flex flex-shrink-0 snap-center flex-col gap-3 rounded-2xl bg-white/[0.02] p-8 md:p-10"
                >
                  <span className="font-mono text-xs tracking-widest text-zinc-500">
                    {String(originalIndex + 1).padStart(2, "0")}
                  </span>
                  <h3 className="text-xl font-semibold text-white md:text-2xl">{p.title}</h3>
                  <p className="text-sm leading-relaxed text-zinc-400 md:text-base">{p.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FRAMEWORK 3-PHASE */}
      <section className="test-snap-section snap-reveal flex min-h-[100dvh] flex-col justify-center border-t border-white/[0.08] py-20">
        <header className="stagger-item mb-12 grid gap-6 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Framework operativo</p>
            <h2 className="mt-3 text-3xl font-bold text-white md:text-4xl">
              Tre fasi, una memoria di progetto unica
            </h2>
          </div>
          <p className="text-zinc-400 md:col-span-7 md:text-lg">
            Una versione operativa del Business Transformation Framework di Deloitte: dalla discovery all'evoluzione
            sostenibile. Le tre fasi non sono cancelli rigidi — il valore di passare da una all'altra è esattamente
            non perdere il contesto accumulato.
          </p>
        </header>
        <div className="grid gap-6 md:grid-cols-3">
          {phases.map((ph) => (
            <article
              key={ph.step}
              className="stagger-item flex h-full flex-col gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6"
            >
              <p className="font-mono text-xs tracking-widest text-zinc-500">{ph.step}</p>
              <h3 className="text-xl font-semibold text-white">{ph.title}</h3>
              <p className="text-sm leading-relaxed text-zinc-400">{ph.body}</p>
              <ul className="mt-2 space-y-2 text-sm text-zinc-300">
                {ph.bullets.map((b) => (
                  <li key={b} className="flex gap-2">
                    <span className="mt-2 inline-block h-1 w-1 flex-shrink-0 rounded-full bg-zinc-500" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* KPI 3-COL */}
      <section className="test-snap-section snap-reveal flex min-h-[100dvh] flex-col justify-center border-t border-white/[0.08] py-20">
        <header className="stagger-item mb-12 max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Misurazione</p>
          <h2 className="mt-3 text-3xl font-bold text-white md:text-4xl">
            Tre dimensioni di KPI, non una sola
          </h2>
          <p className="mt-4 text-zinc-400 md:text-lg">
            La sola creazione di valore non basta. Le trasformazioni che si stabilizzano misurano in parallelo anche
            la salute del team e il progresso del cambiamento — i due indicatori che spiegano in anticipo perché
            qualcosa funziona o si arena.
          </p>
        </header>
        <div className="grid gap-8 md:grid-cols-3">
          {kpiBuckets.map((k, i) => (
            <div key={k.label} className="stagger-item space-y-3 border-t border-white/[0.08] pt-6">
              <p className="font-mono text-xs tracking-widest text-zinc-500">
                K{String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="text-lg font-semibold text-white">{k.label}</h3>
              <p className="text-sm leading-relaxed text-zinc-400">{k.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 4 SEZIONI ESTESE — header */}
      <section className="test-snap-section snap-reveal flex min-h-[100dvh] flex-col justify-center border-t border-white/[0.08] py-20">
        <header className="max-w-3xl">
          <p className="stagger-item text-[11px] uppercase tracking-[0.2em] text-zinc-500">Cosa portiamo</p>
          <h2 className="stagger-item mt-3 text-4xl font-bold text-white md:text-6xl">
            Quattro pratiche operative
          </h2>
          <p className="stagger-item mt-6 text-zinc-400 md:text-xl">
            Ogni progetto si struttura intorno a queste quattro pratiche. Non sono fasi sequenziali ma assi su cui
            misurare la qualità di ciò che produciamo, indipendentemente dal settore.
          </p>
        </header>
      </section>

      {sectionsExpanded.map((item, idx) => (
        <section
          key={item.id}
          className="test-snap-section snap-reveal flex min-h-[100dvh] flex-col justify-center border-t border-white/[0.08] py-20"
        >
          <article className="grid gap-8 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-2">
              <span className="stagger-item font-mono text-sm tracking-widest text-zinc-500">
                {String(idx + 1).padStart(2, "0")}
              </span>
            </div>
            <div className="space-y-4 md:col-span-6">
              <p className="stagger-item text-[11px] uppercase tracking-[0.2em] text-zinc-500">{item.kicker}</p>
              <h3 className="stagger-item text-3xl font-bold text-white md:text-4xl">{item.title}</h3>
              <p className="stagger-item max-w-xl text-zinc-300 md:text-lg">{item.body}</p>
              <p className="stagger-item max-w-xl text-sm leading-relaxed text-zinc-500">{item.details}</p>
            </div>
            <ul className="stagger-item space-y-2 border-l border-white/[0.08] pl-5 text-sm text-zinc-300 md:col-span-4">
              {item.list.map((l) => (
                <li key={l} className="flex gap-2">
                  <span className="mt-2 inline-block h-1 w-1 flex-shrink-0 rounded-full bg-zinc-500" />
                  <span>{l}</span>
                </li>
              ))}
            </ul>
          </article>
        </section>
      ))}

      {/* BIONIC COMPANY 2-COL PROSE */}
      <section className="test-snap-section snap-reveal flex min-h-[100dvh] flex-col justify-center gap-10 border-t border-white/[0.08] py-20 md:grid md:grid-cols-12">
        <div className="stagger-item md:col-span-4">
          <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Modello di riferimento</p>
          <h2 className="mt-3 text-3xl font-bold leading-tight text-white md:text-4xl">
            Bionic company: tecnologia + capacità umane
          </h2>
        </div>
        <div className="stagger-item space-y-4 text-zinc-400 md:col-span-8 md:text-[17px] md:leading-relaxed">
          <p>
            BCG definisce <em className="text-zinc-200 not-italic">bionic company</em> un'organizzazione che fonde
            tecnologia avanzata e capacità umane per trasformare operations, esperienza cliente e modello di offerta.
            Non è un'etichetta di marketing: è un set di pratiche misurabili.
          </p>
          <p>
            Le bionic company hanno mostrato un vantaggio quantitativo significativo. Un terzo di esse ha incrementato
            l'enterprise value di oltre il 10%, contro il 15% dei laggard. Due terzi riportano un ROI sui progetti
            digitali superiore al 10%, contro poco più di un terzo dei concorrenti meno maturi digitalmente.
          </p>
          <p>
            Il motore di questo divario non è la dimensione del budget tech ma la qualità della selezione dei KPI —
            spesso interamente nuovi rispetto al passato — e la presenza di un control tower che li mantiene
            visibili a tutta l'organizzazione. Le aziende che fanno cattivo uso dei KPI tendono a tracciarne troppi e
            scollegati dalla strategia.
          </p>
          <p>
            La traduzione operativa per chi avvia oggi un percorso simile è disciplina. Pochi indicatori, tutti
            collegati alla strategia, una sola dashboard — non una suite di strumenti scollegati — e una cadenza di
            revisione costante.
          </p>
        </div>
      </section>

      {/* FAQ 2-COL */}
      <section className="test-snap-section snap-reveal flex min-h-[100dvh] flex-col justify-center border-t border-white/[0.08] py-20">
        <header className="stagger-item mb-12 max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Domande ricorrenti</p>
          <h2 className="mt-3 text-3xl font-bold text-white md:text-4xl">Quello che ci chiedono più spesso</h2>
        </header>
        <div className="grid gap-x-12 gap-y-8 md:grid-cols-2">
          {faq.map((f) => (
            <div key={f.q} className="stagger-item space-y-2">
              <h3 className="text-lg font-semibold text-white">{f.q}</h3>
              <p className="text-sm leading-relaxed text-zinc-400">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FONTI */}
      <section className="test-snap-section snap-reveal flex min-h-[100dvh] flex-col justify-center border-t border-white/[0.08] py-16">
        <header className="stagger-item mb-8 max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Fonti</p>
          <h2 className="mt-3 text-2xl font-bold text-white md:text-3xl">Riferimenti pubblici utilizzati</h2>
          <p className="mt-3 text-sm text-zinc-500">
            I principi, le percentuali e i framework citati in questa pagina sono adattati da pubblicazioni
            disponibili pubblicamente:
          </p>
        </header>
        <ul className="grid gap-3 text-sm text-zinc-400 md:grid-cols-2">
          <li className="stagger-item">
            <a
              className="underline decoration-zinc-700 underline-offset-4 transition hover:text-white hover:decoration-white"
              href="https://www.mckinsey.com/featured-insights/mckinsey-explainers/what-is-digital-transformation"
              target="_blank"
              rel="noreferrer"
            >
              McKinsey · What is digital transformation?
            </a>
          </li>
          <li className="stagger-item">
            <a
              className="underline decoration-zinc-700 underline-offset-4 transition hover:text-white hover:decoration-white"
              href="https://www.mckinsey.com/capabilities/tech-and-ai/our-insights/leadership-and-digital-transformation"
              target="_blank"
              rel="noreferrer"
            >
              McKinsey · Rewiring for digital and AI
            </a>
          </li>
          <li className="stagger-item">
            <a
              className="underline decoration-zinc-700 underline-offset-4 transition hover:text-white hover:decoration-white"
              href="https://www2.deloitte.com/us/en/pages/operations/solutions/business-transformation-offering.html"
              target="_blank"
              rel="noreferrer"
            >
              Deloitte · Business Transformation Services
            </a>
          </li>
          <li className="stagger-item">
            <a
              className="underline decoration-zinc-700 underline-offset-4 transition hover:text-white hover:decoration-white"
              href="https://www.deloitte.com/us/en/services/consulting/services/business-transformation-solutions.html"
              target="_blank"
              rel="noreferrer"
            >
              Deloitte · Business Transformation Solutions
            </a>
          </li>
          <li className="stagger-item">
            <a
              className="underline decoration-zinc-700 underline-offset-4 transition hover:text-white hover:decoration-white"
              href="https://www.bcg.com/publications/2021/digital-acceleration-index"
              target="_blank"
              rel="noreferrer"
            >
              BCG · Digital Acceleration Index
            </a>
          </li>
          <li className="stagger-item">
            <a
              className="underline decoration-zinc-700 underline-offset-4 transition hover:text-white hover:decoration-white"
              href="https://www.bcg.com/press/13february2024-organizations-using-ai-to-create-kpis-report-kpi-improvement"
              target="_blank"
              rel="noreferrer"
            >
              BCG · 90% of organizations using AI to create KPIs report KPI improvement
            </a>
          </li>
        </ul>
      </section>

      {/* CTA */}
      <section className="test-snap-section flex min-h-[100dvh] flex-col justify-center border-t border-white/[0.08] py-24">
        <div className="snap-reveal flex flex-col items-start gap-5">
          <h2 className="stagger-item text-3xl font-bold text-white md:text-5xl">Pronti a partire?</h2>
          <p className="stagger-item max-w-xl text-zinc-400 md:text-lg">
            Apriamo una conversazione: in 30 minuti capiamo se possiamo essere il partner giusto. Niente impegno,
            nessuna call di vendita travestita.
          </p>
          <a
            className="stagger-item rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#0a0a0c]"
            href="/contact"
          >
            Contattaci
          </a>
        </div>
      </section>
    </div>
  );
}
